import { useMutation } from "react-query";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";

// Helper function to generate UUID (with fallback for older browsers)
const generateUUID = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback UUID v4 generator
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// Helper function to generate unique filename with UUID
const generateUniqueFileName = (originalFileName) => {
  const lastDotIndex = originalFileName.lastIndexOf(".");
  const hasExtension =
    lastDotIndex > 0 && lastDotIndex < originalFileName.length - 1;

  let fileExtension = "";
  if (hasExtension) {
    fileExtension = originalFileName.substring(lastDotIndex + 1).toLowerCase();
  }

  const uuid = generateUUID();
  return hasExtension ? `${uuid}.${fileExtension}` : uuid;
};

// Hook to get presigned URL
const usePresignedUrl = () => {
  return useMutation({
    mutationFn: async ({ category, fileName, contentType }) => {
      const res = await axiosInstance.post(ApiEndPoints.UPLOAD.presignedUrl, {
        category,
        fileName,
        contentType,
      });
      return res.data.data;
    },
  });
};

// Hook to confirm upload
const useConfirmUpload = () => {
  return useMutation({
    mutationFn: async ({ fileKey, category }) => {
      const res = await axiosInstance.post(ApiEndPoints.UPLOAD.confirm, {
        fileKey,
        category,
      });
      return res.data.data;
    },
  });
};

// Hook to upload file using presigned URL with progress tracking
export const usePresignedUpload = () => {
  const { mutate: getPresignedUrl } = usePresignedUrl();
  const { mutate: confirmUpload } = useConfirmUpload();

  return useMutation({
    mutationFn: async ({ file, category, onProgress }) => {
      return new Promise((resolve, reject) => {
        // Generate unique filename with UUID to avoid conflicts
        const fileName = generateUniqueFileName(file.name);
        const contentType = file.type || "image/png";

        // Step 1: Get presigned URL with UUID filename
        getPresignedUrl(
          { category, fileName, contentType },
          {
            onSuccess: async (presignedData) => {
              try {
                // Step 2: Upload file to S3 using presigned URL
                const url = new URL(presignedData.uploadUrl);
                const urlContentType = url.searchParams.get("Content-Type");
                const uploadContentType = urlContentType
                  ? decodeURIComponent(urlContentType)
                  : contentType;

                // Use XMLHttpRequest for S3 upload with progress tracking
                await new Promise((resolve, reject) => {
                  const xhr = new XMLHttpRequest();

                  xhr.open("PUT", presignedData.uploadUrl, true);
                  xhr.setRequestHeader("Content-Type", uploadContentType);

                  // Track upload progress
                  xhr.upload.onprogress = (event) => {
                    if (event.lengthComputable && onProgress) {
                      const percentCompleted = Math.round(
                        (event.loaded * 100) / event.total
                      );
                      onProgress(percentCompleted);
                    }
                  };

                  xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                      // S3 upload successful
                      resolve();
                    } else {
                      const errorMessage = xhr.responseText || xhr.statusText;
                      console.error("S3 Upload Error:", {
                        status: xhr.status,
                        statusText: xhr.statusText,
                        responseText: xhr.responseText,
                        uploadUrl: presignedData.uploadUrl,
                        contentType: uploadContentType,
                      });
                      reject(
                        new Error(
                          `Failed to upload file to S3: ${xhr.status} ${xhr.statusText}. ${errorMessage}`
                        )
                      );
                    }
                  };

                  xhr.onerror = (event) => {
                    const isCorsError = xhr.status === 0 && !xhr.responseText;
                    console.error("S3 Upload CORS Error:", {
                      isCorsError,
                      status: xhr.status,
                      uploadUrl: presignedData.uploadUrl,
                      contentType: uploadContentType,
                      origin: window.location.origin,
                    });

                    if (isCorsError) {
                      const corsConfig = {
                        AllowedHeaders: ["Content-Type"],
                        AllowedMethods: ["PUT", "OPTIONS"],
                        AllowedOrigins: [
                          window.location.origin,
                          "http://localhost:3000",
                        ],
                        ExposeHeaders: ["ETag"],
                        MaxAgeSeconds: 3000,
                      };

                      reject(
                        new Error(
                          `CORS Error: S3 bucket must be configured to allow PUT requests from origin '${window.location.origin}'. ` +
                            `Please configure CORS on the S3 bucket with: ${JSON.stringify(
                              corsConfig,
                              null,
                              2
                            )}`
                        )
                      );
                    } else {
                      reject(
                        new Error(
                          `Network error while uploading file to S3: ${xhr.status} ${xhr.statusText}`
                        )
                      );
                    }
                  };

                  xhr.ontimeout = () => {
                    reject(new Error("Upload to S3 timed out after 30 seconds"));
                  };

                  xhr.onabort = () => {
                    reject(new Error("Upload to S3 was aborted"));
                  };

                  xhr.timeout = 30000;
                  xhr.send(file);
                });

                // Step 3: Confirm upload if S3 upload succeeded
                confirmUpload(
                  {
                    fileKey: presignedData.fileKey,
                    category,
                  },
                  {
                    onSuccess: (confirmData) => {
                      // Ensure fileKey starts with "/" for proper storage format
                      const fileKey = confirmData.fileKey.startsWith("/")
                        ? confirmData.fileKey
                        : `/${confirmData.fileKey}`;

                      resolve({
                        ...confirmData,
                        fileUrl: confirmData.fileUrl,
                        fileKey: fileKey,
                        fileName: file.name,
                        fileSize: file.size,
                        fileType: file.type,
                      });
                    },
                    onError: (error) => {
                      reject(error);
                    },
                  }
                );
              } catch (error) {
                reject(error);
              }
            },
            onError: (error) => {
              reject(error);
            },
          }
        );
      });
    },
  });
};
